import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Menu, MenuItem, Tab, Tabs } from "@mui/material";
import type { NavDataType } from "@/constants/common";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  NAV_BAR_COLORS,
  NAV_BAR_LAYOUT,
  NAV_BAR_MENU_PAPER_SX,
  NAV_BAR_POPUP_IDS,
  NAV_BAR_TYPOGRAPHY,
} from "../nav-bar-constant";

export interface NavBarTabsProps {
  navOptions: NavDataType[];
  active: string;
  anchorEl: HTMLElement | null;
  openMenuFor: string | null;
  onTabClick: (tab: NavDataType, event: React.MouseEvent<HTMLElement>) => void;
  onCloseMenu: (subMenuItem?: NavDataType) => void;
}

const NavBarTabs = ({
  navOptions,
  active,
  anchorEl,
  openMenuFor,
  onTabClick,
  onCloseMenu,
}: NavBarTabsProps) => {
  const openSubMenu = navOptions.find(
    (it) => it.value === openMenuFor
  )?.children;

  return (
    <>
      <Tabs
        value={active}
        variant="scrollable"
        scrollButtons="auto"
        textColor="inherit"
        sx={{
          minHeight: NAV_BAR_LAYOUT.TAB_HEIGHT,
          alignSelf: "flex-end",
          "& .MuiTabs-flexContainer": { gap: NAV_BAR_LAYOUT.TAB_STRIP_GAP },
          "& .MuiTabs-scrollButtons": { color: NAV_BAR_COLORS.ON_BAR },
          "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
        }}
        slotProps={{
          indicator: { sx: { display: "none" } },
        }}
      >
        {navOptions.map((tab) => {
          const isActive = tab.value === active;
          const isMenuOpen = openMenuFor === tab.value && Boolean(anchorEl);
          const TabIcon = tab.icon;
          const ExpandIcon = isMenuOpen ? ExpandLessIcon : ExpandMoreIcon;

          return (
            <Tab
              key={tab.value}
              value={tab.value}
              disableRipple
              onClick={(event) => onTabClick(tab, event)}
              aria-haspopup={tab.children ? "menu" : undefined}
              aria-expanded={tab.children ? isMenuOpen : undefined}
              sx={{
                minHeight: NAV_BAR_LAYOUT.TAB_HEIGHT,
                height: NAV_BAR_LAYOUT.TAB_HEIGHT,
                minWidth: "auto",
                px: NAV_BAR_LAYOUT.TAB_PADDING_X,
                py: 0,
                opacity: 1,
                borderRadius: NAV_BAR_LAYOUT.TAB_RADIUS,
                color: isActive
                  ? NAV_BAR_COLORS.TAB_ACTIVE_TEXT
                  : NAV_BAR_COLORS.ON_BAR,
                backgroundColor: isActive
                  ? NAV_BAR_COLORS.TAB_ACTIVE_BG
                  : "transparent",
                "&.Mui-selected": { color: NAV_BAR_COLORS.TAB_ACTIVE_TEXT },
                "&:hover": {
                  backgroundColor: isActive
                    ? NAV_BAR_COLORS.TAB_ACTIVE_BG
                    : NAV_BAR_COLORS.TAB_HOVER_BG,
                },
              }}
              label={
                <Flex alignItems="center" gap={NAV_BAR_LAYOUT.TAB_GAP}>
                  {TabIcon && (
                    <TabIcon
                      sx={{ fontSize: NAV_BAR_LAYOUT.TAB_ICON_SIZE }}
                      aria-hidden
                    />
                  )}
                  <Text
                    fontSize={NAV_BAR_TYPOGRAPHY.LABEL_FONT_SIZE}
                    fontWeight={NAV_BAR_TYPOGRAPHY.LABEL_FONT_WEIGHT}
                    c="inherit"
                    noWrap
                  >
                    {tab.label}
                  </Text>
                  {!!tab.children?.length && (
                    <ExpandIcon
                      sx={{ fontSize: NAV_BAR_LAYOUT.TAB_ICON_SIZE }}
                    />
                  )}
                </Flex>
              }
            />
          );
        })}
      </Tabs>

      <Menu
        id={NAV_BAR_POPUP_IDS.SUB_MENU}
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && !!openSubMenu?.length}
        onClose={() => onCloseMenu()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: { sx: NAV_BAR_MENU_PAPER_SX },
        }}
      >
        {openSubMenu?.map((option) => (
          <MenuItem key={option.value} onClick={() => onCloseMenu(option)}>
            <Text
              fontSize={NAV_BAR_TYPOGRAPHY.LABEL_FONT_SIZE}
              c={NAV_BAR_COLORS.MENU_ITEM_TEXT}
            >
              {option.label}
            </Text>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default NavBarTabs;
